import React, { useEffect, useMemo } from 'react'
import { Helmet } from 'react-helmet-async'
import { NavLink, useParams } from 'react-router-dom'
import styled, { useTheme } from 'styled-components'
import { ContentWrapper, Flex, Text, TextVariant } from '@mprolab/uikit'
import { useThemeMediaQuery } from 'hooks/useThemeMediaQuery'
import {
  DOCUMENTATION_DEFAULT_SLUG,
  documentationRegistry,
  getDocumentationItemBySlug,
} from './registry'
import { MarkdownBody } from './MarkdownBody'
import { buildHeadingOutline } from './headingOutline'

const PageRoot = styled(Flex)`
  flex-direction: column;
  padding: 64px 0 96px;
  margin-top: 82px;
`

const TwoColumn = styled(Flex)`
  align-items: flex-start;
  gap: 32px;
  width: 100%;

  ${({ theme }) => theme.mediaQueries.tablet} {
    flex-direction: column;
    gap: 24px;
  }
`

const Sidebar = styled.aside`
  flex: 0 0 260px;
  max-width: 280px;
  position: sticky;
  top: 96px;
  align-self: flex-start;
  max-height: calc(100vh - 120px);
  overflow-y: auto;
  padding-right: 8px;

  ${({ theme }) => theme.mediaQueries.tablet} {
    position: static;
    max-height: none;
    max-width: none;
    flex: none;
    width: 100%;
    border-bottom: 1px solid ${({ theme }) => theme.colors.gray6};
    padding-bottom: 16px;
  }
`

const MainColumn = styled.main`
  flex: 1;
  min-width: 0;
  max-width: 820px;
`

const GroupTitle = styled(Text)`
  display: block;
  margin: 16px 0 8px;
  font-weight: 600;
  &:first-child {
    margin-top: 0;
  }
`

const StyledNavLink = styled(NavLink)`
  display: block;
  padding: 8px 10px;
  margin-bottom: 4px;
  border-radius: 8px;
  text-decoration: none;
  color: ${({ theme }) => theme.colors.gray3};
  font-size: 14px;
  line-height: 1.35;
  transition: background 0.15s ease, color 0.15s ease;

  &:hover {
    color: ${({ theme }) => theme.colors.gray1};
    background: ${({ theme }) => theme.colors.gray7};
  }

  &.active {
    color: ${({ theme }) => theme.colors.gray1};
    background: ${({ theme }) => theme.colors.gray7};
    font-weight: 600;
  }
`

const ArticleToc = styled.nav`
  margin-bottom: 28px;
  padding: 16px 18px;
  border-radius: 12px;
  background: ${({ theme }) => theme.colors.gray7};
  border: 1px solid ${({ theme }) => theme.colors.gray6};
`

const ArticleTocTitle = styled.span`
  display: block;
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.gray4};
  margin-bottom: 12px;
`

const TocList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
`

const TocItem = styled.li<{ $level: 2 | 3 }>`
  margin: 0 0 6px;
  padding-left: ${({ $level }) => ($level === 3 ? '14px' : '0')};
`

const TocLink = styled.a`
  font-size: 14px;
  line-height: 1.4;
  color: ${({ theme }) => theme.colors.cyan};
  text-decoration: none;
  &:hover {
    text-decoration: underline;
    text-underline-offset: 2px;
  }
`

const Documentation: React.FC = () => {
  const { slug } = useParams<{ slug: string }>()
  const theme = useTheme()
  const isTablet = useThemeMediaQuery('tablet')

  // An unknown slug shows the default article rather than redirecting: a
  // <Navigate> rendered inside PageWrapper is cancelled by the wrapper's own
  // path push, which is what left the bare /documentation route blank.
  const item = getDocumentationItemBySlug(slug) ?? getDocumentationItemBySlug(DOCUMENTATION_DEFAULT_SLUG)
  const headingOutline = useMemo(
    () => (item ? buildHeadingOutline(item.markdown) : []),
    [item?.markdown],
  )

  useEffect(() => {
    if (item && !isTablet) {
      window.scrollTo({ top: 0, behavior: 'smooth' })
    }
  }, [item?.slug, isTablet])

  if (!item) return null

  return (
    <>
      <Helmet>
        <title>{`${item.title} — Community docs | MPRO Lab`}</title>
        <meta
          name="description"
          content="Community-first guides for MPRO Lab: MPRO, licenses, DAO, marketplace, and Terminal—written for NODE licensees and newcomers, not auditors."
        />
      </Helmet>
      <Flex justifyContent="center" width="100%">
        <ContentWrapper width="100%">
          <PageRoot>
            <Text variant={TextVariant.H4} mb="8px" color={theme.colors.gray1}>
              Community documentation
            </Text>
            <Text variant={TextVariant.BODY_SMALL} mb="24px" color={theme.colors.gray3}>
              Written for NODE licensees and newcomers: how the app works, what the DAO is building, and how we grow together.
              Pick a topic on the left; each article opens with quick jumps to sections below.
            </Text>
            <TwoColumn>
              <Sidebar aria-label="Community documentation topics">
                {documentationRegistry.map((group) => (
                  <div key={group.id}>
                    <GroupTitle variant={TextVariant.BODY_SMALL} color={theme.colors.gray4}>
                      {group.title}
                    </GroupTitle>
                    {group.items.map((doc) => (
                      <StyledNavLink key={doc.slug} to={`/documentation/${doc.slug}`}>
                        {doc.title}
                      </StyledNavLink>
                    ))}
                  </div>
                ))}
              </Sidebar>
              <MainColumn>
                {headingOutline.length > 0 && (
                  <ArticleToc aria-label="On this page">
                    <ArticleTocTitle>Jump to a section</ArticleTocTitle>
                    <TocList>
                      {headingOutline.map((h) => (
                        <TocItem key={h.id} $level={h.level}>
                          <TocLink href={`#${h.id}`}>{h.text}</TocLink>
                        </TocItem>
                      ))}
                    </TocList>
                  </ArticleToc>
                )}
                <MarkdownBody markdown={item.markdown} outline={headingOutline} />
              </MainColumn>
            </TwoColumn>
          </PageRoot>
        </ContentWrapper>
      </Flex>
    </>
  )
}

export default Documentation
